import { Howl } from "howler";

// Importar os arquivos de áudio corretamente para React
// Caminho relativo de src/helpers/ para src/assets/
import soundFile from "../assets/sound.mp3";
import alertBetacutFile from "../assets/alert-betacut.mp3";
import gtaSoundFile from "../assets/gta-sound.mp3";
import bellFile from "../assets/bell.mp3";
import livechatFile from "../assets/livechat.mp3";
import phoneRingFile from "../assets/phone-ring.mp3";

// Configurações
const CONFIG = {
    DEFAULT_VOLUME: 50,
    MIN_INTERVAL_MS: 500,
    MAX_VOLUME: 100,
    MIN_VOLUME: 0,
    STORAGE_KEY: "volume"
};

// Lista de sons disponíveis para validação
const AVAILABLE_SOUNDS = [
    'sound', 'alertBetacut', 'gtaSound',
    'bell', 'livechat', 'phoneRing'
];

// Mapeamento dos arquivos importados
const SOUND_FILES = {
    sound: soundFile,
    alertBetacut: alertBetacutFile,
    gtaSound: gtaSoundFile,
    bell: bellFile,
    livechat: livechatFile,
    phoneRing: phoneRingFile
};

// Função para criar sons com tratamento de erro
const createSound = (src, name) => {
    try {
        // Verificar se o arquivo foi importado corretamente
        if (!src) {
            console.error(`Arquivo de áudio '${name}' não foi encontrado`);
            return null;
        }

        return new Howl({
            src: [src],
            html5: true,
            preload: true,
            onloaderror: (id, error) => {
                console.error(`Erro ao carregar áudio '${name}':`, error);
            },
            onload: () => {
                // console.log(`Áudio '${name}' carregado com sucesso`);
            }
        });
    } catch (error) {
        console.error(`Erro ao criar instância do som '${name}':`, error);
        return null;
    }
};

// Inicialização segura dos sons
const initializeSounds = () => {
    const sounds = {};

    for (const [key, filePath] of Object.entries(SOUND_FILES)) {
        const sound = createSound(filePath, key);
        if (sound) {
            sounds[key] = sound;
        } else {
            console.warn(`Som '${key}' não pôde ser carregado`);
        }
    }

   // console.log(`Sons carregados: ${Object.keys(sounds).join(', ')}`);
    return sounds;
};

const sounds = initializeSounds();

// Estado do player - usando Map para controlar por ticket ID
const activeSounds = new Map();
let lastCallTime = 0;

// Função para obter volume do localStorage de forma segura
const getVolumeFromStorage = () => {
    try {
        if (typeof Storage === 'undefined') {
            return CONFIG.DEFAULT_VOLUME;
        }

        const storedVolume = localStorage.getItem(CONFIG.STORAGE_KEY);

        if (storedVolume === null) {
            return CONFIG.DEFAULT_VOLUME;
        }

        const parsedVolume = Number(storedVolume);

        if (isNaN(parsedVolume)) {
            return CONFIG.DEFAULT_VOLUME;
        }

        return Math.max(CONFIG.MIN_VOLUME, Math.min(CONFIG.MAX_VOLUME, parsedVolume));
    } catch (error) {
        console.error('Erro ao acessar localStorage:', error);
        return CONFIG.DEFAULT_VOLUME;
    }
};

// Função para validar parâmetro de entrada
const validateNotification = (notification) => {
    if (!notification) {
        throw new Error('Parâmetro notification é obrigatório');
    }

    if (typeof notification !== 'string') {
        throw new Error('Parâmetro notification deve ser uma string');
    }

    if (!AVAILABLE_SOUNDS.includes(notification)) {
        throw new Error(
            `Som '${notification}' não está disponível. ` +
            `Sons disponíveis: ${AVAILABLE_SOUNDS.join(', ')}`
        );
    }

    if (!sounds[notification]) {
        throw new Error(`Som '${notification}' não foi carregado corretamente`);
    }
};

// Função para parar som atual de forma segura
const stopSoundByTicketId = (ticketId) => {
    try {
        if (activeSounds.has(ticketId)) {
            const { soundInstance, soundId } = activeSounds.get(ticketId);
            if (soundInstance && typeof soundInstance.stop === "function") {
                soundInstance.stop(soundId);
            }
            activeSounds.delete(ticketId);
        }
    } catch (error) {
        console.error('Erro ao parar som:', error);
        activeSounds.delete(ticketId);
    }
};

// Função para tocar som de forma segura
const playSound = (soundInstance, ticketId) => {
    try {
        const soundId = soundInstance.play();

        // Verificar se o som realmente começou a tocar
        if (soundId === undefined || soundId === null) {
            console.error('Falha ao iniciar reprodução do som');
            return null;
        }

        // Armazenar referência do som ativo com o ID
        activeSounds.set(ticketId, { soundInstance, soundId });

        // Adicionar listeners de erro para este som específico
        soundInstance.once('playerror', (id, error) => {
            console.error('Erro durante reprodução:', error);
            activeSounds.delete(ticketId);
        });

        soundInstance.once('end', () => {
            activeSounds.delete(ticketId);
        });

        return soundId;
    } catch (error) {
        console.error('Erro ao reproduzir som:', error);
        return null;
    }
};

// Função principal
const NotificationSound = (notification, ticketId = 'default') => {
    try {
        // Validação de entrada
        validateNotification(notification);

        // Validar ticketId
        if (ticketId === null || ticketId === undefined) {
            ticketId = 'default';
        }

        // Obter e aplicar volume
        const volumeValue = getVolumeFromStorage();
        const adjustedVolume = volumeValue / 100;

        const soundInstance = sounds[notification];

        // Verificar se o som está pronto
        if (soundInstance.state() !== 'loaded') {
            console.warn(`Som '${notification}' ainda não está carregado. Estado: ${soundInstance.state()}`);
        }

        soundInstance.volume(adjustedVolume);

        // Retornar função de reprodução
        return () => {
            try {
                const currentTime = Date.now();

                // Verificar intervalo mínimo
                if (currentTime - lastCallTime < CONFIG.MIN_INTERVAL_MS) {
                    console.log('Som ignorado: intervalo muito curto');
                    return;
                }

                // Parar som atual para este ticket (se existir)
                stopSoundByTicketId(ticketId);

                // Verificar se o som está pronto para tocar
                if (soundInstance.state() !== 'loaded') {
                    console.warn(`Tentando tocar som não carregado: ${notification}`);
                    return;
                }

                // Reproduzir novo som
                const soundId = playSound(soundInstance, ticketId);

                if (soundId !== null) {
                    lastCallTime = currentTime;
                    console.log(`Som '${notification}' iniciado para ticket ${ticketId}`);
                } else {
                    console.error(`Falha ao reproduzir som '${notification}'`);
                }

            } catch (error) {
                console.error('Erro na função de reprodução:', error);
            }
        };

    } catch (error) {
        console.error('Erro no NotificationSound:', error);

        // Retornar função segura que não faz nada
        return () => {
            console.warn(`Reprodução de som desabilitada devido a erro: ${error.message}`);
        };
    }
};

export { NotificationSound };